import { useLoaderData, useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { Skeleton } from "primereact/skeleton";
import FamilyTopNavbar from "./components/FamilyTopNavbar";
import accountsAPI from "../../services/accounts";

export function FamilySchoolPage() {
  const { school } = useLoaderData();
  const params = useParams();
  const navigate = useNavigate();
  const schoolId = params.schoolId;
  const [familyData, setFamilyData] = useState(null);
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFamilyData = async () => {
      try {
        setLoading(true);

        const response = await accountsAPI.GetCurrentAccountFamily(schoolId);
        
        if (response.success) {
          setFamilyData(response.data);
          
          // Extract trainings from registrations
          const trainingsFromRegistrations = response.data.registrations.map(registration => ({
            ...registration.training,
            students: registration.students
          }));
          
          setTrainings(trainingsFromRegistrations);
        } else {
          throw new Error("Erreur dans la réponse de l'API");
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching family data:", err);
        setError(err.message || "Erreur lors du chargement des données familiales");
        setLoading(false);
      }
    };

    fetchFamilyData();
  }, [schoolId]);

  const getStatusColor = (status) => {
    switch (status) {
      case "OPEN":
        return "success";
      case "DRAFT":
        return "info";
      case "SUSPENDED":
        return "warning";
      case "ARCHIVED":
        return "contrast";
      default:
        return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "OPEN":
        return "En cours";
      case "DRAFT":
        return "A venir";
      case "SUSPENDED":
        return "Suspendue";
      case "ARCHIVED":
        return "Archivée";
      default:
        return status;
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case "OPEN":
        return "bg-green-50 border-green-200";
      case "DRAFT":
        return "bg-blue-50 border-blue-200";
      case "SUSPENDED":
        return "bg-yellow-50 border-yellow-200";
      case "ARCHIVED":
        return "bg-gray-50 border-gray-200";
      default:
        return "bg-white border-gray-200";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fr-FR");
  };

  const activeTrainings = trainings.filter(t => t.status === "OPEN" || t.status === "DRAFT");
  const archivedTrainings = trainings.filter(t => t.status === "ARCHIVED");

  const TrainingCard = ({ training }) => (
    <Card 
      className={`${getStatusBgColor(training.status)} hover:shadow-lg transition-shadow duration-200 cursor-pointer border-1 w-full`}
      onClick={() => {
        navigate(`/school/${schoolId}/family/training/${training.id}`);
      }}
    >
      <div className="p-2">
        <div className="flex justify-between items-start mb-3">
          <Tag
            value={getStatusLabel(training.status)}
            severity={getStatusColor(training.status)}
            className="text-xs"
          />
        </div>
        
        <h3 className="text-lg font-semibold text-gray-800 mb-3 line-clamp-2">
          {training.name}
        </h3>
        
        {training.presentation && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-3">
            {training.presentation}
          </p>
        )}
        
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex justify-between">
              <span className="font-medium">Date des cours:</span>
              <span>{formatDate(training.start_courses_date)} - {formatDate(training.end_courses_date)}</span>
            </div>
            
            {(training.status === "OPEN" || training.status === "DRAFT") && (
              <>
                <div className="flex justify-between">
                  <span className="font-medium">Inscriptions:</span>
                  <span>{formatDate(training.start_registration_date)} - {formatDate(training.end_registration_date)}</span>
                </div>
              </>
            )}
          </div>
      </div>
    </Card>
  );

  const TrainingSection = ({ title, trainings }) => {
    // Don't render the section if there are no trainings
    if (trainings.length === 0) {
      return null;
    }

    return (
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 ">
          <i className="pi pi-book mr-2 text-primary"></i>
          {title}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {trainings.map((training) => (
            <div key={training.id} className="w-full">
              <TrainingCard training={training} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ height: "100vh" }}>
        <div className="min-h-screen flex flex-column">
          <FamilyTopNavbar />
          <div className="flex flex-column flex-auto p-3">
            <div className="max-w-4xl mx-auto w-full">
              <div className="mb-6 ">
                <Skeleton height="2rem" className="mb-2" />
                <Skeleton height="1rem" width="60%" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="w-full">
                    <Card className="p-4 w-full">
                      <Skeleton height="1.5rem" className="mb-3" />
                      <Skeleton height="1rem" className="mb-2" />
                      <Skeleton height="1rem" width="80%" />
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: "100vh" }}>
        <div className="min-h-screen flex flex-column">
          <FamilyTopNavbar />
          <div className="flex flex-column flex-auto p-3">
            <div className="max-w-4xl mx-auto w-full">
              <div className=" py-8">
                <i className="pi pi-exclamation-triangle text-5xl text-red-500 mb-4"></i>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Erreur</h2>
                <p className="text-gray-600 mb-4">{error}</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
                >
                  Réessayer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: "100vh" }}>
      <div style={{ height: "100vh" }}>
        <div className="min-h-screen flex flex-column">        
          <div className="flex flex-column flex-auto p-3">
            <div className="flex flex-column align-items-center w-full h-full">
              <div className="w-full h-full" style={{ maxWidth: '990px' }}>
              <div className="flex flex-column lg:flex-row gap-4">
                 <div className="flex-none lg:w-3 w-full">
                   <Card>
                     <div className="flex flex-column align-items-center p-2">
                       <img src={school?.avatar || "/images/school-empty-logo.svg"} alt="School Logo" className="w-8" style={{ maxWidth: '200px' }} />
                       <span className="text-xl font-medium mt-3 text-center block">{school?.name}</span>
                       <span className="text-sm text-500 mt-3">{school?.presentation}</span>
                       <div className="text-center mt-3">
                         <hr className="w-6" />
                         <div className="text-sm text-500 mt-2">
                           <div>{school?.address}</div>
                           <div>{school?.city}, {school?.postal_code}</div>
                           <div>{school?.phone}</div>
                           <div>{school?.email}</div>
                         </div>
                       </div>
                     </div>
                   </Card>
                 </div>
                <div className="flex-1 w-full">
                  

                   {/* Active and Draft Trainings */}
                   <TrainingSection
                     title="Formations en cours et à venir"
                     trainings={activeTrainings}
                   />

                   {/* Archived Trainings */}
                   <TrainingSection
                     title="Formations archivées"
                     trainings={archivedTrainings}
                   />

                   {/* Show message if no trainings at all */}
                   {activeTrainings.length === 0 && archivedTrainings.length === 0 && !loading && (
                     <div className="text-center py-8 text-gray-500">
                       <i className="pi pi-info-circle text-3xl mb-2"></i>
                       <p>Aucune formation disponible pour le moment</p>
                     </div>
                   )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  );
}
